import RouteInfo from '@ember/routing/-private/route-info';
import RouteInfoWithAttributes from '@ember/routing/-private/route-info-with-attributes';
import CampaignModel from 'cio/models/campaign';
import { Events as events, EventType } from 'cio/utils/analytics/events';
import { UTM_PARAMS } from 'cio/utils/analytics/utm';

const EVENT_NAMES: { [key: string]: EventType } = {
  'signup.index': events.signup.started,

  'actions.email.smtp.index': events.email.smtp_tab_viewed,
  'actions.email.suppression_list': events.email.suppressions_tab_viewed,
  'actions.email.sending_domains': events.email.sending_domains_tab_viewed,
  'actions.url_params.edit': events.message.url_params_edit_started,
  'actions.messaging_limit.index': events.workspace.message_limits_viewed,

  // Events inside broadcasts that should actually send campaign events.
  'broadcasts.broadcast.overview.workflow': events.campaign.workflow_tab_viewed,
  'broadcasts.broadcast.setup.workflow.actions': events.campaign.workflow_tab_viewed,

  // Events inside broadcasts that could be either API triggered broadcasts or newsletters.
  'broadcasts.broadcast.overview.index': events.broadcast.overview_tab_viewed,
  'broadcasts.broadcast.overview.recipients.index': events.broadcast.recipients_tab_viewed,
  'broadcasts.broadcast.overview.drafts.index': events.broadcast.drafts_tab_viewed,
  'broadcasts.broadcast.overview.goal.index': events.broadcast.goal_tab_viewed,
  'broadcasts.broadcast.overview.triggering_details.index': events.broadcast.triggering_details_tab_viewed,
  'broadcasts.broadcast.overview.subjects.index': events.broadcast.journeys_tab_viewed,
  'broadcasts.broadcast.setup.recipients.index': events.broadcast.recipients_tab_viewed,
  'broadcasts.broadcast.setup.goal.index': events.broadcast.goal_tab_viewed,
  'broadcasts.broadcast.setup.review.index': events.broadcast.review_tab_viewed,
  'broadcasts.newsletter.overview.reports.index': events.broadcast.overview_tab_viewed,
  'broadcasts.newsletter.overview.sent.index': events.broadcast.sent_tab_viewed,
  'broadcasts.newsletter.overview.content.index': events.broadcast.content_tab_viewed,
  'broadcasts.newsletter.overview.recipients.index': events.broadcast.recipients_tab_viewed,
  'broadcasts.newsletter.overview.test.index': events.broadcast.test_tab_viewed,
  'broadcasts.newsletter.setup.recipients.index': events.broadcast.recipients_tab_viewed,
  'broadcasts.newsletter.setup.content.index': events.broadcast.content_tab_viewed,
  'broadcasts.newsletter.setup.tracking.index': events.broadcast.goal_tab_viewed,
  'broadcasts.newsletter.setup.review.index': events.broadcast.review_tab_viewed,

  'campaigns.campaign.overview.index': events.campaign.overview_viewed,
  'campaigns.campaign.overview.trigger.index': events.campaign.trigger_tab_viewed,
  'campaigns.campaign.overview.workflow.actions': events.campaign.workflow_tab_viewed,
  'campaigns.campaign.overview.sent.index': events.campaign.sent_tab_viewed,
  'campaigns.campaign.overview.drafts.index': events.campaign.drafts_tab_viewed,
  'campaigns.campaign.overview.goal.index': events.campaign.goal_and_exit_tab_viewed,
  'campaigns.campaign.overview.settings.index': events.campaign.settings_tab_viewed,
  'campaigns.campaign.overview.metrics': events.campaign.metrics_tab_viewed,
  'campaigns.campaign.overview.subjects.index': events.campaign.journeys_tab_viewed,
  'campaigns.campaign.overview.subjects.subject': events.campaign.specific_webhook_journey_viewed,
  'campaigns.campaign.overview.tests.index': events.campaign.ab_tests_tab_viewed,
  'campaigns.campaign.setup.trigger.index': events.campaign.trigger_tab_viewed,
  'campaigns.campaign.setup.workflow.actions': events.campaign.workflow_tab_viewed,
  'campaigns.campaign.setup.goal.index': events.campaign.goal_and_exit_tab_viewed,
  'campaigns.campaign.setup.settings.index': events.campaign.settings_tab_viewed,
  'campaigns.campaign.setup.review.index': events.campaign.review_tab_viewed,
  'campaigns.new': events.campaign.create_started,

  dashboard: events.dashboard.viewed,
  'composer.newsletter.template': events.message.newsletter_edit_content,
  'composer.action': events.message.campaign_edit_content,
  'exports.index': events.export.list_viewed,

  'imports.import.setup.index': events.import.upload_viewed,
  'imports.import.setup.fields': events.import.map_fields_viewed,
  'imports.import.setup.review': events.import.review_viewed,
  'imports.import.setup.preview': events.import.event_preview_viewed,
  'imports.index': events.import.list_viewed,
  'imports.new': events.import.upload_viewed,

  'integrations-public.index': events.integration.list_viewed_public,
  'integrations.index': events.integration.list_viewed,
  'integrations.segment_in.success': events.integration.segment_in_success_viewed,
  'logs.list': events.logs.viewed,
  snippets: events.workspace.snippets_viewed,
  'snippets.new': events.snippets.creation_started,
  'snippets.snippet': events.snippets.detail_page_viewed,
  'data-index.type.list.index': events.logs.data_index_viewed,
  'data-index.type.item': events.logs.data_index_item_viewed,
  'outbox.list.deliveries': events.logs.deliveries_or_drafts_viewed,
  'outbox.list.drafts': events.logs.deliveries_or_drafts_viewed,

  'people.new': events.people.manual_person_creation_started,
  'people.merge': events.people.merge_setup_tab_viewed,
  'person.index': events.people.person_overview_viewed,
  'person.attributes.index': events.people.person_attributes_viewed,
  'person.segments.belongs': events.people.person_segments_viewed,
  'person.segments.others': events.people.person_segments_viewed,
  'person.devices': events.people.person_devices_viewed,
  'person.drafts': events.people.person_drafts_viewed,
  'person.relationships': events.people.person_relationships_viewed,
  'person.sent': events.people.person_sent_viewed,
  'person.activity': events.people.person_activity_viewed,
  'person.subjects.index': events.people.person_journeys_viewed,
  'person.subjects.subject': events.people.specific_person_journey_viewed,

  'objects.type.new': events.objects.object_create_started,
  'object.index': events.objects.object_overview_tab_viewed,
  'object.attributes.index': events.objects.object_attributes_tab_viewed,
  'object.attributes.edit': events.objects.object_displayed_attribute_edit_started,
  'object.activity': events.objects.object_activity_tab_viewed,
  'object.relationships': events.objects.object_relationship_tab_viewed,

  'segments.index': events.segment.list_viewed_searched_or_filtered,
  'segments.new': events.segment.creation_started,
  'segments.segment.overview.index': events.segment.overview_tab_viewed,
  'segments.segment.overview.edit': events.segment.edit_started,
  'segments.segment.usage': events.segment.usage_tab_viewed,
  'segments.segment.audiences': events.segment.ad_audiences_tab_viewed,
  'segments.segment.people': events.segment.people_tab_viewed,

  'settings.index': events.account.settings_viewed,
  'settings.personal.index': events.user.settings_viewed,
  'settings.personal.security.new': events.user.two_fa_viewed,
  'settings.receipts.index': events.user.billing_history_viewed,
  'settings.security.index': events.account.security_settings_viewed,
  'settings.security.sso': events.account.security_sso_settings_viewed,
  'settings.workspaces.new': events.workspace.creation_started,
  'workspace-settings.index': events.workspace.settings_viewed,
  'workspace-settings.edit': events.workspace.general_settings_viewed,
  'workspace-settings.language': events.workspace.language_settings_viewed,
  'workspace-settings.subscription_center.topics': events.subscription_center.topics_viewed,
  'workspace-settings.subscription_center.branding_edit': events.subscription_center.branding_page_viewed,
  'settings.privacy': events.account.privacy_viewed,
  'settings.billing.your-plan': events.account.your_plan_viewed,
  'settings.billing.billing-history': events.account.billing_history_viewed,
  'settings.billing.available-plans.journeys': events.account.available_plans_viewed,
  'settings.billing.payment-methods': events.account.payment_methods_viewed,

  'setup-guide.index': events.onboarding.page_viewed,
  'setup-guide.sources': events.onboarding.page_viewed,
  'setup-guide.channels.index': events.onboarding.page_viewed,
  'setup-guide.channels.email': events.onboarding.page_viewed,
  'setup-guide.channels.slack': events.onboarding.page_viewed,
  'setup-guide.channels.twilio': events.onboarding.page_viewed,
  'setup-guide.channels.webhook': events.onboarding.page_viewed,
  'setup-guide.channels.push': events.onboarding.page_viewed,
  'setup-guide.create-campaign': events.onboarding.page_viewed,
  'setup-guide.send-campaign': events.onboarding.page_viewed,

  'collections.index': events.collections.list_viewed,

  analysis: events.analysis.viewed,

  no_accessible_workspaces: events.user.no_accessible_workspaces,

  'transactional.join-beta': events.transactional.join_beta_viewed,
  'transactional.index': events.transactional.index_viewed,
  'transactional.message.overview.index': events.transactional.overview_tab_viewed,
  'transactional.message.overview.metrics': events.transactional.metrics_tab_viewed,
  'transactional.message.overview.drafts': events.transactional.drafts_tab_viewed,
  'transactional.message.overview.sent.index': events.transactional.sent_tab_viewed,
  'transactional.new': events.transactional.create_started,
  'transactional.message.setup.index': events.transactional.add_content_step_viewed,
  'transactional.message.setup.settings': events.transactional.configure_settings_step_viewed,
  'transactional.message.setup.review': events.transactional.send_message_step_viewed,

  'composer.transactional.template': events.transactional.transactional_composer_viewed,

  'integrations.warehouse.type.index': events.warehouse.settings_viewed,
  'integrations.warehouse.snowflake': events.warehouse.destination_viewed,
  'integrations.warehouse.redshift': events.warehouse.destination_viewed,
  'integrations.warehouse.bigquery': events.warehouse.destination_viewed,
  'integrations.warehouse.type.sync': events.warehouse.create_sync_started,

  'sql-imports.new': events.sql_imports.create_started,
  'sql-imports.import.index': events.sql_imports.imports_tab_viewed,
  'sql-imports.import.query': events.sql_imports.query_tab_viewed,
  'sql-imports.import.relationships-query': events.sql_imports.relationship_query_tab_viewed,
  'sql-imports.import.settings': events.sql_imports.settings_tab_viewed,

  'message-library': events.library.page_viewed,
};

export const PASS_THROUGH_QUERY_PARAMS: Record<string, string[]> = {
  'signup.index': ['signupType'],
  'segments.segment.people': ['source'],
  'campaigns.campaign.overview.trigger.index': ['source'],
  'campaigns.campaign.overview.workflow.actions': ['source'],
  'campaigns.campaign.overview.goal.index': ['source'],
  'campaigns.campaign.overview.tests.index': ['source'],
  'people.merge': ['source'],
};

// Some events need to be skipped based on the route/model's state. This is a
// catch-all for those events.
function skipEvent(route: RouteInfo, previousRouteName: string, previousRoute: RouteInfo | null): boolean {
  // If the new route and the previous route are the same, and the previous
  // route had utm query params, but the new one doesn't - don't send a tracking
  // event.
  // This means that the "app" route's didTransition action fired and has reset
  // the utm query params to their defaults, and we don't want to track that.
  if (
    previousRoute &&
    previousRouteName === route.name &&
    Object.keys(previousRoute.queryParams).some((key) => UTM_PARAMS.includes(key)) &&
    Object.keys(route.queryParams).every((key) => !UTM_PARAMS.includes(key))
  ) {
    return true;
  }

  // Do not send an event when the "Create Ad Audience" modal is brought up.
  if (route.name === 'segments.segment.audiences' && route.queryParams.isAdding) {
    return true;
  }

  // When a route's query params change, it still counts as a transition, and that still
  // sends an event.
  //
  // For many routes, we don't want that behavior, so we return true for `skipEvent` if
  // the current route and previous routes match.
  let exactMatchRoutes = [
    'segments.segment.audiences',
    'segments.segment.people',
    'campaigns.index',
    'broadcasts.list.api-triggered.index',
    'broadcasts.list.newsletters.index',
    'campaigns.campaign.overview.index',
    'campaigns.campaign.overview.metrics',
    'integrations-public.index',
    'integrations.index',
    'settings.personal.index',
    'setup-guide.sources',
    'setup-guide.channels.push',
    'analysis',
    'actions.email.sending_domains',
    'transactional.message.overview.index',
    'transactional.message.overview.metrics',
    'sql-imports.import.index',
    'sql-imports.import.query',
    'sql-imports.import.settings',
    'people.merge',
    'exports.index',
  ];

  // Substring routes will match any route that have one of these names in them. For example:
  // workflow.actions matches workflow.actions.index, campaign.workflow.actions, broadcast.workflow.actions, etc.
  //
  // ... and so on.
  let substringRoutes = ['workflow.actions'];

  if (
    (exactMatchRoutes.includes(route.name) || substringRoutes.any((routeName) => route.name.includes(routeName))) &&
    previousRouteName === route.name
  ) {
    return true;
  }

  if (route.name === 'campaigns.campaign.overview.subjects.subject') {
    if ((route.parent as RouteInfoWithAttributes).attributes instanceof CampaignModel) {
      // Only trigger the "journey viewed" event if the campaign is
      // webhook-triggered
      let model = (route.parent as RouteInfoWithAttributes).attributes as CampaignModel;
      return !model.isWebhookTriggered;
    }
  }

  return false;
}

export default function generateTransitionEvent(
  route: RouteInfo,
  { previousRouteName, previousRoute }: { previousRouteName: string; previousRoute: null },
) {
  let event = EVENT_NAMES[route.name];
  if (!event || skipEvent(route, previousRouteName, previousRoute)) {
    return;
  }

  return event;
}
